import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../config/supabase";
import {
  checkLoginAttempts,
  recordLoginAttempt,
  getRemainingLockoutTime,
} from "../utils/adminSecurity";

// Using Supabase Auth instead of hardcoded credentials

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLocked, setIsLocked] = useState(!checkLoginAttempts());
  const [lockoutTime, setLockoutTime] = useState(getRemainingLockoutTime());
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }

    // Check if account is locked
    if (!checkLoginAttempts()) {
      setError(
        `Account temporarily locked. Try again in ${getRemainingLockoutTime()} minutes.`
      );
      return;
    }

    // Attempt Supabase authentication
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: username.trim(),
      password,
    });

    if (authError || !data.session) {
      recordLoginAttempt(false);
      setError("Invalid credentials");
      if (!checkLoginAttempts()) {
        setIsLocked(true);
        setLockoutTime(getRemainingLockoutTime());
      }
      return;
    }

    // Successful login
    recordLoginAttempt(true);
    navigate("/admin/dashboard");
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Admin Panel
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLocked}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLocked ? `Locked (${lockoutTime}m)` : "Sign In"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLogin;
